import { useEffect, useState, useMemo } from "react";
import { api, logAudit } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isEditor } from "@/lib/roles";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Users, Phone, Mail, Award, Search, Plus, Trash2, Shield, RefreshCw, Globe, Cloud } from "lucide-react";
import { toast } from "sonner";
import { useAwayMap, awayFor, AwayBadge } from "@/components/AwayBadge";

const BLANK = { name: "", title: "", practice_group: "", bar_admissions: "", extension: "", email: "", backup_contact: "", reports_to: "", photo_url: "", bio: "" };

function Avatar({ src, name, cls }) {
  const [err, setErr] = useState(false);
  const ini = (name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("");
  if (!src || err) return <span className={`grid place-items-center ${cls} rounded-sm bg-primary text-primary-foreground font-display font-bold shrink-0`}>{ini}</span>;
  return <img src={src} alt={name} onError={() => setErr(true)} className={`${cls} rounded-sm object-cover pointer-events-none shrink-0`} />;
}

export default function People() {
  const { user } = useAuth();
  const editor = isEditor(user?.role);
  const [staff, setStaff] = useState([]);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("all");
  const [form, setForm] = useState(BLANK);
  const [open, setOpen] = useState(false);
  const awayMap = useAwayMap();

  const load = () => api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => ["all", ...Array.from(new Set(staff.map((s) => s.practice_group).filter(Boolean)))], [staff]);
  const filtered = staff.filter((s) => {
    const matchesQ = !q || `${s.name} ${s.title} ${s.practice_group} ${s.bar_admissions}`.toLowerCase().includes(q.toLowerCase());
    const matchesG = group === "all" || s.practice_group === group;
    return matchesQ && matchesG;
  });

  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    try {
      await api.post("/staff", form);
      toast.success("Team member added");
      setOpen(false); setForm(BLANK); load();
    } catch { toast.error("Could not save"); }
  };
  const remove = async (id) => { await api.delete(`/staff/${id}`); load(); toast.success("Removed"); };

  const syncWebsite = async () => {
    try { const { data } = await api.post("/staff/sync/website"); toast.success(`Synced ${data.synced} people from the firm website`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
  };
  const syncEntra = async () => {
    try { const { data } = await api.post("/staff/sync/entra"); toast.success(`Synced ${data.synced} users from Entra ID`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Entra sync failed"); }
  };

  const initials = (n) => n.split(" ").map((x) => x[0]).slice(0, 2).join("");

  return (
    <div data-testid="people-page">
      <PageHeader overline="People & HR" title="Staff Directory" subtitle="Attorneys and staff — bar admissions, extensions, and backup contacts.">
        {user?.role === "admin" && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="sync-directory-btn"><RefreshCw className="h-4 w-4 mr-1.5" /> Sync Directory</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem data-testid="sync-website" onClick={syncWebsite}><Globe className="h-4 w-4 mr-2" /> Sync from firm website</DropdownMenuItem>
              <DropdownMenuItem data-testid="sync-entra" onClick={syncEntra}><Cloud className="h-4 w-4 mr-2" /> Sync from Entra ID (Office 365)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {editor && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-person-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Person</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                {[["name","Full Name"],["title","Title"],["practice_group","Practice Group"],["bar_admissions","Bar Admissions"],["extension","Extension"],["email","Email"],["backup_contact","Backup Contact"],["reports_to","Reports To"],["photo_url","Photo URL"]].map(([k,l]) => (
                  <div key={k} className={k==="photo_url"?"col-span-2":""}>
                    <Label className="text-xs">{l}</Label>
                    <Input data-testid={`person-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                  </div>
                ))}
              </div>
              <DialogFooter><Button data-testid="save-person-btn" onClick={save}>Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="people-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, title, bar…" className="pl-9" />
        </div>
        <Select value={group} onValueChange={setGroup}>
          <SelectTrigger className="w-56" data-testid="people-filter-group"><SelectValue /></SelectTrigger>
          <SelectContent>
            {groups.map((g) => <SelectItem key={g} value={g}>{g === "all" ? "All Practice Groups" : g}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Users} title="No people found" hint="Try a different search or filter." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="people-grid">
          {filtered.map((s) => (
            <div key={s.id} className="border border-border bg-card rounded-sm p-5 hover:border-primary transition-colors animate-fade-up" data-testid="person-card">
              <div className="flex items-start gap-4">
                <Avatar src={s.photo_url} name={s.name} cls="h-14 w-14" />
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold leading-tight">{s.name}</div>
                  <div className="text-sm text-primary">{s.title}</div>
                  <div className="text-xs text-muted-foreground">{s.practice_group}</div>
                  <AwayBadge away={awayFor(awayMap, s.email)} className="mt-1.5" />
                </div>
                {editor && (
                  <button data-testid="delete-person-btn" onClick={() => remove(s.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
              <div className="mt-4 space-y-1.5 text-sm">
                {s.bar_admissions && s.bar_admissions !== "—" && <div className="flex items-center gap-2 text-muted-foreground"><Award className="h-3.5 w-3.5" /> Bar: {s.bar_admissions}</div>}
                {s.extension && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="h-3.5 w-3.5" /> Ext. <span className="font-condensed font-semibold">{s.extension}</span></div>}
                {s.email && <div className="flex items-center gap-2 text-muted-foreground truncate"><Mail className="h-3.5 w-3.5" /> {s.email}</div>}
                {s.backup_contact && <div className="flex items-center gap-2 text-muted-foreground"><Shield className="h-3.5 w-3.5" /> Backup: {s.backup_contact}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
